import { useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { updateCursoFavorite } from "Slices/Curso/cursoSlice";
import { updatePaqueteFavorite } from "Slices/PaqueteCursos/paqueteCursosSlice";
import { addFavorite } from "Services/add_favorite_service";

/**
 * Hook personalizado para manejar favoritos con estado de procesamiento
 * @param {number} idUsuario - ID del usuario autenticado
 * @returns {Object} Funciones y estado del hook
 * @returns {Function} returns.toggleFavorite - Función para alternar favorito
 * @returns {Function} returns.isItemProcessing - Función para verificar si un item está procesando
 */
export const useFavorite = (idUsuario) => {
  const dispatch = useDispatch();
  const [processingItems, setProcessingItems] = useState(new Set());

  /**
   * Verifica si un item específico está siendo procesado
   * @param {string} itemKey - Clave del item (idAlterno)
   * @returns {boolean} - True si el item está siendo procesado
   */
  const isItemProcessing = useCallback(
    (itemKey) => {
      return processingItems.has(itemKey);
    },
    [processingItems]
  );

  /**
   * Alterna el estado de favorito de un curso/paquete
   * @param {Object} item - Objeto del curso o paquete
   */
  const toggleFavorite = useCallback(
    (item) => {
      if (!idUsuario) {
        return;
      }

      const itemKey = item.idAlterno;

      if (processingItems.has(itemKey)) return;

      const isCourse = item.hasOwnProperty("idCurso");
      const idAprendizaje = isCourse ? item.idCurso : item.idPaquete;
      const idTipoAprendizaje = isCourse ? 1 : 2;
      const currentFavoriteState = Boolean(item.favorito);
      const newFavoriteState = !currentFavoriteState;

      const updateAction = isCourse
        ? updateCursoFavorite
        : updatePaqueteFavorite;
      dispatch(
        updateAction({
          idAlterno: itemKey,
          favorito: newFavoriteState ? 1 : 0,
        })
      );

      setProcessingItems((prev) => new Set(prev).add(itemKey));

      (async () => {
        try {
          const payload = {
            idUsuario,
            idAprendizaje,
            idTipoAprendizaje,
          };

          const response = await addFavorite(payload);
          const realFavoriteState = response.data !== null;

          dispatch(
            updateAction({
              idAlterno: itemKey,
              favorito: realFavoriteState ? 1 : 0,
            })
          );
        } catch (error) {
          console.error("Error al actualizar favorito:", error);
          dispatch(
            updateAction({
              idAlterno: itemKey,
              favorito: currentFavoriteState ? 1 : 0,
            })
          );
        } finally {
          setProcessingItems((prev) => {
            const newSet = new Set(prev);
            newSet.delete(itemKey);
            return newSet;
          });
        }
      })();
    },
    [idUsuario, processingItems, dispatch]
  );

  return {
    toggleFavorite,
    isItemProcessing,
  };
};
